import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';

const poolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || 'PLACEHOLDER_USER_POOL_ID',
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || 'PLACEHOLDER_CLIENT_ID',
};

const userPool = new CognitoUserPool(poolData);

const ID_TOKEN_KEY = 'cognitoIdToken';

// Decode JWT payload (no signature verification — that is done server-side)
function decodeJwtPayload(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

// Normalise Cognito payload into the shape the rest of the app expects
function buildUserInfo(idToken) {
  const payload = decodeJwtPayload(idToken);
  if (!payload) return null;
  return {
    idToken,
    // Keep the same field names that the existing App.js / Header.js use
    mail: payload.email || payload['cognito:username'] || '',
    displayName: payload.name || payload.email || payload['cognito:username'] || '',
    groups: payload['cognito:groups'] || [],
  };
}

/**
 * Sign in with username + password.
 * Returns a normalised userInfo object on success.
 */
export function signIn(username, password) {
  return new Promise((resolve, reject) => {
    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    });

    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: userPool,
    });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (result) => {
        const idToken = result.getIdToken().getJwtToken();
        sessionStorage.setItem(ID_TOKEN_KEY, idToken);
        resolve(buildUserInfo(idToken));
      },
      onFailure: (err) => {
        reject(err);
      },
      // Cognito requires a new password on first login (admin-created accounts)
      newPasswordRequired: (userAttributes, _requiredAttributes) => {
        // Remove read-only / non-writable attributes before completing the challenge
        delete userAttributes.email_verified;
        delete userAttributes.phone_number_verified;
        delete userAttributes.email;          // email is already set; cannot be modified here
        delete userAttributes.phone_number;   // same
        // Resolve (not reject) so the UI can show the "set new password" form
        resolve({ newPasswordRequired: true, cognitoUser, userAttributes });
      },
    });
  });
}

/**
 * Complete the new-password challenge for admin-created accounts.
 * Call this after signIn() returns { newPasswordRequired: true }.
 *
 * @param {CognitoUser} cognitoUser  - the cognitoUser object returned by signIn()
 * @param {string}      newPassword  - the new password chosen by the user
 * @param {object}      userAttributes - the userAttributes returned by signIn()
 * @returns {Promise<userInfo>}
 */
export function completeNewPassword(cognitoUser, newPassword, userAttributes) {
  return new Promise((resolve, reject) => {
    cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
      onSuccess: (result) => {
        const idToken = result.getIdToken().getJwtToken();
        sessionStorage.setItem(ID_TOKEN_KEY, idToken);
        resolve(buildUserInfo(idToken));
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
}

/**
 * Initiate the forgot-password flow.
 * Cognito sends a verification code to the user's registered email.
 *
 * @param {string} username
 * @returns {Promise<void>}
 */
export function forgotPassword(username) {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: userPool,
    });

    cognitoUser.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

/**
 * Complete the forgot-password flow with the verification code.
 *
 * @param {string} username
 * @param {string} code        - the verification code from the email
 * @param {string} newPassword
 * @returns {Promise<void>}
 */
export function confirmForgotPassword(username, code, newPassword) {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: userPool,
    });

    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

/**
 * Sign out the current user and clear stored tokens.
 */
export function signOut() {
  const currentUser = userPool.getCurrentUser();
  if (currentUser) {
    currentUser.signOut();
  }
  sessionStorage.removeItem(ID_TOKEN_KEY);
}

/**
 * Return the stored id_token string, or null if not present.
 */
export function getStoredIdToken() {
  return sessionStorage.getItem(ID_TOKEN_KEY);
}

/**
 * Try to restore an existing valid session (e.g. after a page refresh).
 * Resolves with a normalised userInfo object, or rejects if no valid session.
 */
export function getCurrentSession() {
  return new Promise((resolve, reject) => {
    const currentUser = userPool.getCurrentUser();
    if (!currentUser) {
      reject(new Error('No current user'));
      return;
    }

    currentUser.getSession((err, session) => {
      if (err || !session || !session.isValid()) {
        sessionStorage.removeItem(ID_TOKEN_KEY);
        reject(err || new Error('Session invalid'));
        return;
      }

      const idToken = session.getIdToken().getJwtToken();
      sessionStorage.setItem(ID_TOKEN_KEY, idToken);
      const userInfo = buildUserInfo(idToken);
      if (userInfo) {
        resolve(userInfo);
      } else {
        reject(new Error('Could not decode id token'));
      }
    });
  });
}
